import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { uploadScriptsFile, importMigrateScripts, getMigrateImportStatus } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import '../../../../styles/wizard.css';

const StepM4ImportScripts = () => {
  const navigate = useNavigate();
  const { project, migrate, setMigrate, nextStep, previousStep } = useOnboarding();
  const fileInputRef = useRef(null);
  
  const [uploadMethod, setUploadMethod] = useState('zip'); // zip | git | url
  const [file, setFile] = useState(null);
  const [repoUrl, setRepoUrl] = useState('');
  const [branch, setBranch] = useState('main');
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importJobId, setImportJobId] = useState(migrate.importJobId);
  const [detectedInfo, setDetectedInfo] = useState(null);

  // Poll for import status if we have a job ID
  useEffect(() => {
    if (importJobId && importing) {
      const pollInterval = setInterval(async () => {
        try {
          const response = await getMigrateImportStatus(importJobId);
          if (response.success) {
            const status = response.data.status;
            
            if (status === 'SUCCESS') {
              setDetectedInfo(response.data);
              setMigrate({
                importJobId,
                importStatus: 'SUCCESS',
                frameworkDetected: response.data.framework_detected,
                languageDetected: response.data.language_detected,
                testCount: response.data.test_count,
                runnerDetected: response.data.runner_detected,
                dependencies: response.data.dependencies
              });
              setImporting(false);
              clearInterval(pollInterval);
              toast.success(`Detected ${response.data.test_count} tests!`);
            } else if (status === 'FAILED') {
              setImporting(false);
              clearInterval(pollInterval);
              toast.error('Import failed. Please try again.');
            }
          }
        } catch (error) {
          console.error('Error polling import status:', error);
        }
      }, 2000);

      // Cleanup
      return () => clearInterval(pollInterval);
    }
  }, [importJobId, importing]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.name.endsWith('.zip')) {
        toast.error('Please upload a ZIP file');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUploadZip = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);

    try {
      // Upload file
      const uploadResponse = await uploadScriptsFile(file);
      
      if (uploadResponse.success) {
        const fileId = uploadResponse.data.file_id;
        
        // Start import
        const importResponse = await importMigrateScripts({
          project_id: project.projectId,
          source_type: 'ZIP',
          file_id: fileId
        });

        if (importResponse.success) {
          const jobId = importResponse.data.import_job_id;
          setImportJobId(jobId);
          setImporting(true);
          toast.info('Analyzing your scripts...');
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleImportGit = async () => {
    if (!repoUrl) {
      toast.error('Please enter a repository URL');
      return;
    }

    setUploading(true);

    try {
      const importResponse = await importMigrateScripts({
        project_id: project.projectId,
        source_type: 'GIT',
        repo_url: repoUrl,
        branch: branch || 'main'
      });

      if (importResponse.success) {
        const jobId = importResponse.data.import_job_id;
        setImportJobId(jobId);
        setImporting(true);
        toast.info('Analyzing your repository...');
      }
    } catch (error) {
      console.error('Error importing from Git:', error);
      toast.error('Import failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleContinue = () => {
    if (!detectedInfo || detectedInfo.test_count === 0) {
      toast.error('No tests detected. Please upload a valid automation project.');
      return;
    }

    nextStep();
    navigate('/app/onboarding?step=5');
  };

  const canContinue = detectedInfo && detectedInfo.test_count > 0;

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={4} completedSteps={[1, 2, 3]} />
        
        <WizardCard
          title="Import your automation code"
          subtitle="Upload your existing test automation scripts"
        >
          {/* Upload Method Selection */}
          <div style={{ marginBottom: '2rem' }}>
            <div className="tab-buttons" style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
              <button
                type="button"
                className={`tab-button ${uploadMethod === 'zip' ? 'active' : ''}`}
                onClick={() => setUploadMethod('zip')}
              >
                📦 Upload ZIP
              </button>
              <button
                type="button"
                className={`tab-button ${uploadMethod === 'git' ? 'active' : ''}`}
                onClick={() => setUploadMethod('git')}
              >
                🔗 Git Repository
              </button>
            </div>

            {/* ZIP Upload */}
            {uploadMethod === 'zip' && (
              <div>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: '2px dashed var(--border-color)',
                    borderRadius: '8px',
                    padding: '3rem 2rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: file ? 'var(--success-bg)' : 'var(--bg-gray-50)',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                    {file ? '✓' : '📁'}
                  </div>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {file ? file.name : 'Click to select ZIP file'}
                  </p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Max size: 100MB'}
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                
                {file && !importing && !detectedInfo && (
                  <button
                    onClick={handleUploadZip}
                    disabled={uploading}
                    className="wizard-btn wizard-btn-primary"
                    style={{ width: '100%', marginTop: '1rem' }}
                  >
                    {uploading ? '⏳ Uploading...' : '📤 Upload and Analyze'}
                  </button>
                )}
              </div>
            )}

            {/* Git Repository */}
            {uploadMethod === 'git' && (
              <div>
                {!importing && !detectedInfo && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Repository URL</label>
                      <input
                        type="url"
                        value={repoUrl}
                        onChange={(e) => setRepoUrl(e.target.value)}
                        placeholder="https://github.com/username/repo.git"
                        className="form-control"
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Branch</label>
                      <input
                        type="text"
                        value={branch}
                        onChange={(e) => setBranch(e.target.value)}
                        placeholder="main"
                        className="form-control"
                      />
                    </div>

                    <button
                      onClick={handleImportGit}
                      disabled={uploading || !repoUrl}
                      className="wizard-btn wizard-btn-primary"
                      style={{ whiteSpace: 'nowrap', padding: '0.75rem 1.5rem' }}
                    >
                      {uploading ? '⏳ Connecting...' : '🔗 Connect and Analyze'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Importing Status */}
          {importing && (
            <div style={{
              padding: '2rem',
              background: 'rgba(76, 201, 240, 0.1)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid rgba(76, 201, 240, 0.3)',
                borderTopColor: 'var(--success-color)',
                borderRadius: '50%',
                margin: '0 auto 1rem',
                animation: 'spin 1s linear infinite'
              }} />
              <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                Analyzing your automation scripts...
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Detecting framework, language, and tests
              </p>
            </div>
          )}

          {/* Detection Results */}
          {detectedInfo && (
            <div style={{ marginTop: '1.5rem' }}>
              {detectedInfo.test_count > 0 ? (
                <div style={{
                  padding: '1.5rem',
                  background: 'white',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px'
                }}>
                  {/* Compact Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    paddingBottom: '1rem',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid var(--border-color)'
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      color: 'var(--success-color)'
                    }}>
                      ✓
                    </div>
                    <h4 style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: 'var(--success-color)',
                      margin: 0
                    }}>
                      Scripts Detected!
                    </h4>
                  </div>

                  {/* 4-Column Stats Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '1rem'
                  }}>
                    {/* Framework */}
                    <div style={{
                      padding: '1rem',
                      background: 'rgba(76, 201, 240, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(76, 201, 240, 0.2)'
                    }}>
                      <div style={{
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem'
                      }}>
                        Framework
                      </div>
                      <div style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        color: 'var(--primary-color)'
                      }}>
                        {detectedInfo.framework_detected}
                      </div>
                    </div>

                    {/* Language */}
                    <div style={{
                      padding: '1rem',
                      background: 'rgba(76, 201, 240, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(76, 201, 240, 0.2)'
                    }}>
                      <div style={{
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem'
                      }}>
                        Language
                      </div>
                      <div style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        color: 'var(--primary-color)'
                      }}>
                        {detectedInfo.language_detected}
                      </div>
                    </div>

                    {/* Tests Found */}
                    <div style={{
                      padding: '1rem',
                      background: 'rgba(76, 201, 240, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(76, 201, 240, 0.2)'
                    }}>
                      <div style={{
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem'
                      }}>
                        Tests Found
                      </div>
                      <div style={{
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        color: 'var(--success-color)'
                      }}>
                        {detectedInfo.test_count}
                      </div>
                    </div>

                    {/* Runner */}
                    <div style={{
                      padding: '1rem',
                      background: 'rgba(76, 201, 240, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(76, 201, 240, 0.2)'
                    }}>
                      <div style={{
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem'
                      }}>
                        Runner
                      </div>
                      <div style={{
                        fontSize: '0.9375rem',
                        fontWeight: '600',
                        fontFamily: 'monospace',
                        color: 'var(--primary-color)'
                      }}>
                        {detectedInfo.runner_detected}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: '1.5rem',
                  background: 'white',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✗</div>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--danger-color)', margin: '0 0 0.5rem 0' }}>
                    No Tests Found
                  </h4>
                  <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9375rem' }}>
                    We couldn't detect runnable tests. Please ensure your repository contains test files.
                  </p>
                </div>
              )}
            </div>
          )}

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=3');
            }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextDisabled={!canContinue}
          />
        </WizardCard>
      </WizardContainer>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .tab-button {
          flex: 1;
          padding: 0.75rem 1.5rem;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          background: white;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .tab-button:hover {
          border-color: var(--primary-color);
          background: rgba(76, 201, 240, 0.05);
        }
        
        .tab-button.active {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }
      `}</style>
    </PrivateWizardLayout>
  );
};

export default StepM4ImportScripts;